import { blendWetInto, dryGround, type WetState } from "odyssey-shared";

/**
 * How long the drawn weather takes at a seam crossing to ease from the state shown to the new region's (ADR-147 note 15):
 * rain, dim, puddles, soak and wet ground. A door switches at once behind its own fade.
 */
export const WEATHER_FADE_MS = 4000;

/**
 * The curve a fade follows: a smoothstep, flat at both ends so neither the start nor the end of the change shows as a step.
 *
 * @param t The time run, 0 to 1; clamped.
 * @returns The mix, 0 to 1.
 */
export function fadeEase(t: number): number {
  const k = t <= 0 ? 0 : t >= 1 ? 1 : t;
  return k * k * (3 - 2 * k);
}

/**
 * One fade of the drawn weather from what was shown to a new region's weather. Holds its two ground states in objects made
 * once, so a frame of a fade allocates nothing. The strength fades towards the live target, which may move during the fade;
 * the ground fades towards the new region's state read once when the fade begins.
 */
export class WeatherFade {
  private startMs = 0;
  private spanMs = 0;
  private fromStrength = 0;
  private running = false;
  private readonly fromGround: WetState = dryGround();
  private readonly toGround: WetState = dryGround();

  /**
   * Whether a fade is running.
   *
   * @returns True between `begin` and `end`.
   */
  public get active(): boolean {
    return this.running;
  }

  /**
   * Begins a fade.
   *
   * @param nowMs The clock at the crossing, in milliseconds.
   * @param spanMs How long the fade runs.
   * @param strength The strength drawn at the crossing.
   * @param shown The ground drawn at the crossing; copied.
   * @param target The new region's ground at the crossing; copied.
   */
  public begin(nowMs: number, spanMs: number, strength: number, shown: Readonly<WetState>, target: Readonly<WetState>): void {
    this.startMs = nowMs;
    this.spanMs = spanMs;
    this.fromStrength = strength;
    Object.assign(this.fromGround, shown);
    Object.assign(this.toGround, target);
    this.running = true;
  }

  /**
   * Stops the fade; what is drawn follows the target alone.
   */
  public end(): void {
    this.running = false;
  }

  /**
   * Whether the fade has run its span.
   *
   * @param nowMs The clock.
   * @returns True once the span has passed.
   */
  public done(nowMs: number): boolean {
    return nowMs - this.startMs >= this.spanMs;
  }

  /**
   * The strength to draw.
   *
   * @param nowMs The clock.
   * @param target The new region's strength now.
   * @returns The eased strength, or the target when no fade runs.
   */
  public strength(nowMs: number, target: number): number {
    if (!this.running) return target;
    return this.fromStrength + (target - this.fromStrength) * this.mix(nowMs);
  }

  /**
   * Writes the ground to draw.
   *
   * @param nowMs The clock.
   * @param out The state written.
   * @returns `out`.
   */
  public groundInto(nowMs: number, out: WetState): WetState {
    return blendWetInto(this.fromGround, this.toGround, this.mix(nowMs), out);
  }

  /**
   * The ground shown at the crossing, which the fade runs from.
   *
   * @returns The state; read only.
   */
  public get from(): Readonly<WetState> {
    return this.fromGround;
  }

  /**
   * The new region's ground at the crossing, which the fade runs to.
   *
   * @returns The state; read only.
   */
  public get to(): Readonly<WetState> {
    return this.toGround;
  }

  /**
   * The eased mix at a time.
   *
   * @param nowMs The clock.
   * @returns 0 to 1.
   */
  public mix(nowMs: number): number {
    return this.spanMs <= 0 ? 1 : fadeEase((nowMs - this.startMs) / this.spanMs);
  }
}
